import { ReactElement, useEffect, useMemo } from 'react';
import { Spin } from 'antd';
import { updateAbility } from 'app/atoms';
import { PermissionPropTypes } from './types';
import { useUserInfo } from 'app/hooks/useUserInfo';
import { Permissions as UserPermissions } from 'app/modules/Auth/types';

export interface IPermissions {
  action: string;
  subject: string;
}

/**
 * Maps the permissions object from UserInfoResponse to the array format required by updateAbility
 * @param permissions - The permissions object from UserInfoResponse
 * @returns Array of permission objects with action and subject
 */
const mapPermissionsToAbilityFormat = (permissions: UserPermissions): IPermissions[] => {
  const abilityPermissions: IPermissions[] = [];

  // Iterate through each category in the permissions object
  for (const [categoryName, category] of Object.entries(permissions)) {
    // Only process categories that are visible
    if (category.isVisible && category.permissions) {
      // Iterate through each permission in the category
      for (const [permissionKey, hasPermission] of Object.entries(category.permissions)) {
        // Only add permissions that are enabled (true)
        if (hasPermission) {
          abilityPermissions.push({
            action: permissionKey,
            subject: categoryName,
          });
        }
      }
    }
  }

  return abilityPermissions;
};

const UserPermission: React.FC<PermissionPropTypes> = (props: PermissionPropTypes): ReactElement => {
  const { children } = props;

  const { data: userInfo, isLoading } = useUserInfo();

  // Memoize the mapped permissions to avoid recalculating on every render
  const abilityPermissions = useMemo(() => {
    if (userInfo?.permissions) {
      return mapPermissionsToAbilityFormat(userInfo.permissions);
    }
    return [];
  }, [userInfo?.permissions]);

  // Update ability when permissions change
  useEffect(() => {
    if (abilityPermissions.length > 0) {
      updateAbility(abilityPermissions);
    }
  }, [abilityPermissions]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75">
        <Spin size="large" />
      </div>
    );
  }

  return children;
};

export default UserPermission;
